import { useState, useEffect, useCallback, useRef } from 'react';
import AppLayout from '@/components/AppLayout';
import { useAuth } from '@/context/AuthContext';
import { API, getBackendBaseUrl } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  HeadphonesIcon,
  Plus,
  Clock,
  Send,
  CheckCircle,
  AlertTriangle,
  MessageSquare,
  Upload,
  Paperclip,
  X
} from 'lucide-react';

export default function SupportPage() {
  const { user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isDraggingFiles, setIsDraggingFiles] = useState(false);
  const [newAttachments, setNewAttachments] = useState([]);
  const fileInputRef = useRef(null);
  
  // New ticket form
  const [newSubject, setNewSubject] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newCategory, setNewCategory] = useState('general');
  const [newPriority, setNewPriority] = useState('medium');

  const fetchTickets = useCallback(async () => {
    try {
      const response = await fetch(`${API}/support/tickets`, { credentials: 'include' });
      if (response.ok) {
        setTickets(await response.json());
      }
    } catch (error) {
      console.error('Error fetching tickets:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const formatBytes = (bytes) => {
    if (!bytes && bytes !== 0) return '-';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleSelectedAttachments = (files) => {
    const nextFiles = Array.from(files || []).filter(Boolean).slice(0, 5);
    if (nextFiles.length === 0) {
      return;
    }

    const validFiles = [];
    for (const file of nextFiles) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`O arquivo ${file.name} excede 10 MB`);
        continue;
      }
      validFiles.push(file);
    }

    setNewAttachments((current) => {
      const merged = [...current];
      validFiles.forEach((file) => {
        const exists = merged.some((item) => item.name === file.name && item.size === file.size && item.lastModified === file.lastModified);
        if (!exists) {
          merged.push(file);
        }
      });
      return merged.slice(0, 5);
    });
  };

  const removeAttachment = (fileToRemove) => {
    setNewAttachments((current) => current.filter((file) => !(file.name === fileToRemove.name && file.size === fileToRemove.size && file.lastModified === fileToRemove.lastModified)));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  const loadTicketDetail = async (ticket) => {
    setSelectedTicket(ticket);
    try {
      const response = await fetch(`${API}/support/tickets/${ticket.ticket_id}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setMessages(data.messages);
      }
    } catch (error) {
      toast.error('Erro ao carregar mensagens');
    }
  };

  const createTicket = async () => {
    if (!newSubject.trim() || !newDescription.trim()) {
      toast.error('Preencha todos os campos');
      return;
    }

    setSending(true);
    try {
      const formData = new FormData();
      formData.append('subject', newSubject);
      formData.append('description', newDescription);
      formData.append('category', newCategory);
      formData.append('priority', newPriority);
      newAttachments.forEach((file) => {
        formData.append('attachments', file);
      });

      const response = await fetch(`${API}/support/tickets`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (response.ok) {
        toast.success('Ticket criado com sucesso');
        setDialogOpen(false);
        setNewSubject('');
        setNewDescription('');
        setNewCategory('general');
        setNewPriority('medium');
        setNewAttachments([]);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        fetchTickets();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erro ao criar ticket');
      }
    } catch (error) {
      toast.error('Erro ao conectar com o servidor');
    } finally {
      setSending(false);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedTicket) return;

    setSending(true);
    try {
      const response = await fetch(`${API}/support/tickets/${selectedTicket.ticket_id}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ content: newMessage })
      });

      if (response.ok) {
        const msg = await response.json();
        setMessages([...messages, msg]);
        setNewMessage('');
        toast.success('Mensagem enviada');
      }
    } catch (error) {
      toast.error('Erro ao enviar mensagem');
    } finally {
      setSending(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      open: 'bg-orange-100 text-orange-700',
      in_progress: 'bg-blue-100 text-blue-700',
      resolved: 'bg-emerald-100 text-emerald-700',
      closed: 'bg-zinc-100 text-zinc-700'
    };
    const labels = {
      open: 'Aberto',
      in_progress: 'Em Andamento',
      resolved: 'Resolvido',
      closed: 'Fechado'
    };
    return <Badge className={`${styles[status]} border-0`}>{labels[status]}</Badge>;
  };

  const getPriorityBadge = (priority) => {
    const styles = {
      urgent: 'bg-red-100 text-red-700',
      high: 'bg-orange-100 text-orange-700',
      medium: 'bg-amber-100 text-amber-700',
      low: 'bg-emerald-100 text-emerald-700'
    };
    return <Badge className={`${styles[priority]} border-0 text-xs`}>{priority}</Badge>;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('pt-BR', { 
      day: '2-digit', 
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDuration = (startStr, endStr = null) => {
    if (!startStr) return '-';
    const start = new Date(startStr);
    const end = endStr ? new Date(endStr) : new Date();
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return '-';

    const totalMinutes = Math.max(Math.floor((end - start) / (1000 * 60)), 0);
    const days = Math.floor(totalMinutes / (60 * 24));
    const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
    const minutes = totalMinutes % 60;

    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${minutes}min`;
    return `${minutes}min`;
  };

  const getSlaProgress = (ticket) => {
    if (!ticket?.created_at || !ticket?.sla_due_at) {
      return null;
    }

    const openedAt = new Date(ticket.created_at);
    const slaDueAt = new Date(ticket.sla_due_at);
    if (Number.isNaN(openedAt.getTime()) || Number.isNaN(slaDueAt.getTime())) {
      return null;
    }

    const now = new Date();
    const referenceEnd = ['resolved', 'closed'].includes(ticket.status) && ticket.resolved_at
      ? new Date(ticket.resolved_at)
      : now;
    const isResolved = ['resolved', 'closed'].includes(ticket.status);
    const elapsedMs = Math.max(referenceEnd - openedAt, 0);
    const totalMs = Math.max(slaDueAt - openedAt, 1);
    const remainingMs = slaDueAt - referenceEnd;
    const progress = Math.min(Math.max((elapsedMs / totalMs) * 100, 0), 100);

    let tone = 'text-emerald-600 bg-emerald-50';
    if (!isResolved) {
      if (remainingMs < 0) {
        tone = 'text-red-600 bg-red-50';
      } else if (progress >= 85) {
        tone = 'text-orange-600 bg-orange-50';
      } else if (progress >= 60) {
        tone = 'text-amber-600 bg-amber-50';
      }
    } else {
      tone = 'text-emerald-700 bg-emerald-50';
    }

    return {
      progress,
      tone,
      elapsedLabel: formatDuration(ticket.created_at, isResolved ? ticket.resolved_at : null),
      resolutionLabel: isResolved ? formatDuration(ticket.created_at, ticket.resolved_at) : null,
      remainingLabel: remainingMs >= 0
        ? `restam ${formatDuration(referenceEnd.toISOString(), ticket.sla_due_at)}`
        : `estourou há ${formatDuration(ticket.sla_due_at, referenceEnd.toISOString())}`,
    };
  };

  const selectedTicketSla = selectedTicket ? getSlaProgress(selectedTicket) : null;

  return (
    <AppLayout>
      <div className="p-6 md:p-8 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-zinc-900 flex items-center gap-3">
              <HeadphonesIcon className="w-8 h-8 text-violet-600" />
              Suporte
            </h1>
            <p className="text-zinc-500 mt-1">
              Abra um chamado ou acompanhe seus tickets
            </p>
          </div>

          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-full" data-testid="new-ticket-btn">
                <Plus className="w-4 h-4 mr-2" />
                Novo Ticket
              </Button>
            </DialogTrigger>
            <DialogContent className="max-h-[90vh] overflow-hidden flex flex-col">
              <DialogHeader>
                <DialogTitle>Novo Ticket de Suporte</DialogTitle>
                <DialogDescription>
                  Descreva seu problema ou dúvida. Se puder, print a tela e envie junto para analisarmos mais rápido.
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4 py-4 overflow-y-auto pr-1">
                <div className="space-y-2">
                  <Label>Assunto</Label>
                  <Input
                    placeholder="Ex: Problema com importação"
                    value={newSubject}
                    onChange={(e) => setNewSubject(e.target.value)}
                    data-testid="ticket-subject"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Categoria</Label>
                    <Select value={newCategory} onValueChange={setNewCategory}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="general">Geral</SelectItem>
                        <SelectItem value="technical">Técnico</SelectItem>
                        <SelectItem value="billing">Pagamento</SelectItem>
                        <SelectItem value="feature_request">Sugestão</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Prioridade</Label>
                    <Select value={newPriority} onValueChange={setNewPriority}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Baixa</SelectItem>
                        <SelectItem value="medium">Média</SelectItem>
                        <SelectItem value="high">Alta</SelectItem>
                        <SelectItem value="urgent">Urgente</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Descrição</Label>
                  <Textarea
                    placeholder="Descreva seu problema em detalhes. Se houve erro na tela, você pode printar e anexar abaixo."
                    value={newDescription}
                    onChange={(e) => setNewDescription(e.target.value)}
                    className="min-h-[120px]"
                    data-testid="ticket-description"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Anexos</Label>
                  <div
                    className={`rounded-2xl border-2 border-dashed p-5 transition-colors ${isDraggingFiles ? 'border-violet-500 bg-violet-50' : 'border-zinc-200 bg-zinc-50/70'}`}
                    onDragEnter={(e) => {
                      e.preventDefault();
                      setIsDraggingFiles(true);
                    }}
                    onDragOver={(e) => {
                      e.preventDefault();
                      setIsDraggingFiles(true);
                    }}
                    onDragLeave={(e) => {
                      e.preventDefault();
                      setIsDraggingFiles(false);
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      setIsDraggingFiles(false);
                      handleSelectedAttachments(e.dataTransfer.files);
                    }}
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      className="hidden"
                      onChange={(e) => handleSelectedAttachments(e.target.files)}
                    />
                    <div className="flex flex-col items-center gap-3 text-center">
                      <div className="w-12 h-12 rounded-2xl bg-violet-100 text-violet-700 flex items-center justify-center">
                        <Upload className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-zinc-900">Arraste e solte arquivos aqui</p>
                        <p className="text-sm text-zinc-500">Ou selecione no computador. Pode mandar print da tela, PDF, imagem ou áudio.</p>
                      </div>
                      <Button type="button" variant="outline" onClick={() => fileInputRef.current?.click()}>
                        Escolher arquivos
                      </Button>
                      <p className="text-xs text-zinc-400">Até 5 arquivos de 10 MB cada.</p>
                    </div>
                  </div>

                  {newAttachments.length > 0 && (
                    <div className="space-y-2">
                      {newAttachments.map((file) => (
                        <div key={`${file.name}-${file.lastModified}`} className="flex items-center justify-between gap-3 rounded-xl border border-zinc-200 bg-white px-3 py-2">
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-zinc-900 truncate">{file.name}</p>
                            <p className="text-xs text-zinc-500">{formatBytes(file.size)}</p>
                          </div>
                          <Button type="button" variant="ghost" size="icon" onClick={() => removeAttachment(file)}>
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <DialogFooter className="border-t border-zinc-100 pt-4 bg-white">
                <Button variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button onClick={createTicket} disabled={sending} data-testid="submit-ticket">
                  {sending ? 'Criando...' : 'Criar Ticket'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {/* SLA Info */}
        <Card className="mb-6 border-blue-200 bg-blue-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Clock className="w-5 h-5 text-blue-600" />
              <div className="text-sm text-blue-700">
                <strong>SLA de Atendimento:</strong> Urgente (4h) • Alta (8h) • Média (24h) • Baixa (48h)
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Main Content */}
        <div className="grid lg:grid-cols-12 gap-6">
          {/* Tickets List */}
          <div className="lg:col-span-5">
            <Card className="border-zinc-200">
              <CardHeader className="border-b border-zinc-100">
                <CardTitle className="text-lg">Meus Tickets</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <ScrollArea className="h-[500px]">
                  {loading ? (
                    <div className="p-8 text-center">
                      <div className="w-6 h-6 border-2 border-violet-600 border-t-transparent rounded-full animate-spin mx-auto" />
                    </div>
                  ) : tickets.length > 0 ? (
                    <div className="divide-y divide-zinc-100">
                      {tickets.map((ticket) => {
                        const slaProgress = getSlaProgress(ticket);
                        return (
                          <div
                            key={ticket.ticket_id}
                            className={`p-4 cursor-pointer hover:bg-zinc-50 transition-colors ${
                              selectedTicket?.ticket_id === ticket.ticket_id ? 'bg-violet-50' : ''
                            }`}
                            onClick={() => loadTicketDetail(ticket)}
                            data-testid={`ticket-item-${ticket.ticket_id}`}
                          >
                            <div className="flex items-start justify-between gap-2 mb-2">
                              <p className="text-sm font-medium text-zinc-900 line-clamp-1">
                                {ticket.subject}
                              </p>
                            </div>
                            <div className="flex items-center gap-2 mb-2">
                              {getStatusBadge(ticket.status)}
                              {getPriorityBadge(ticket.priority)}
                            </div>
                            <div className="space-y-1">
                              <p className="text-xs text-zinc-500">
                                Aberto em {formatDate(ticket.created_at)}
                              </p>
                              {ticket.status === 'resolved' || ticket.status === 'closed' ? (
                                <p className="text-xs text-emerald-600">
                                  Resolvido em {slaProgress?.resolutionLabel || '-'}
                                </p>
                              ) : (
                                <p className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${slaProgress?.tone || 'text-zinc-600 bg-zinc-100'}`}>
                                  Em andamento há {slaProgress?.elapsedLabel || '-'}
                                </p>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="p-8 text-center text-zinc-500">
                      <MessageSquare className="w-10 h-10 mx-auto mb-3 text-zinc-300" />
                      <p>Nenhum ticket ainda</p>
                      <p className="text-sm mt-1">Clique em "Novo Ticket" para abrir um chamado</p>
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>
          </div>

          {/* Ticket Detail & Chat */}
          <div className="lg:col-span-7">
            {selectedTicket ? (
              <Card className="border-zinc-200 h-full">
                <CardHeader className="border-b border-zinc-100">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-mono text-xs text-zinc-500 mb-1">
                        #{selectedTicket.ticket_id}
                      </p>
                      <CardTitle className="text-lg">{selectedTicket.subject}</CardTitle>
                    </div>
                    <div className="flex items-center gap-2">
                      {getStatusBadge(selectedTicket.status)}
                    </div>
                  </div>

                  {/* Status indicators */}
                  <div className="flex items-center gap-4 text-xs text-zinc-500 mt-4">
                    <div className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      <span>SLA: {formatDate(selectedTicket.sla_due_at)}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      <span>
                        {selectedTicket.status === 'resolved' || selectedTicket.status === 'closed'
                          ? `Resolvido em ${selectedTicketSla?.resolutionLabel || '-'}`
                          : `Em andamento há ${selectedTicketSla?.elapsedLabel || '-'}`
                        }
                      </span>
                    </div>
                    {selectedTicket.status !== 'resolved' && selectedTicket.status !== 'closed' && selectedTicketSla?.remainingLabel ? (
                      <div className={`rounded-full px-2 py-1 font-medium ${selectedTicketSla?.tone || 'bg-zinc-100 text-zinc-600'}`}>
                        {selectedTicketSla?.remainingLabel}
                      </div>
                    ) : null}
                    {selectedTicket.first_response_at && (
                      <div className="flex items-center gap-1">
                        <CheckCircle className="w-3 h-3 text-emerald-500" />
                        <span>Respondido</span>
                      </div>
                    )}
                  </div>
                </CardHeader>

                <CardContent className="p-4 flex flex-col h-[400px]">
                  {/* Description */}
                  <div className="bg-zinc-50 p-4 rounded-lg mb-4">
                    <p className="text-xs text-zinc-500 mb-1">Descrição:</p>
                    <p className="text-sm text-zinc-700">{selectedTicket.description}</p>
                    {selectedTicket.attachments?.length > 0 && (
                      <div className="mt-4 space-y-2">
                        <p className="text-xs text-zinc-500">Anexos enviados:</p>
                        <div className="space-y-2">
                          {selectedTicket.attachments.map((attachment) => (
                            <a
                              key={attachment.attachment_id}
                              href={`${getBackendBaseUrl()}${attachment.download_url}`}
                              target="_blank"
                              rel="noreferrer"
                              className="flex items-center justify-between gap-3 rounded-xl border border-zinc-200 bg-white px-3 py-2 hover:border-violet-300 hover:bg-violet-50/50"
                            >
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-zinc-900 truncate">{attachment.name}</p>
                                <p className="text-xs text-zinc-500">{formatBytes(attachment.size)}</p>
                              </div>
                              <Paperclip className="w-4 h-4 text-violet-600 flex-shrink-0" />
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Messages */}
                  <ScrollArea className="flex-1 mb-4">
                    <div className="space-y-3">
                      {messages.map((msg) => (
                        <div
                          key={msg.message_id}
                          className={`p-3 rounded-lg ${
                            msg.is_admin 
                              ? 'bg-violet-50 ml-8' 
                              : 'bg-zinc-100 mr-8'
                          }`}
                        >
                          <div className="flex items-center gap-2 mb-1">
                            {msg.is_admin && (
                              <Badge className="bg-violet-100 text-violet-700 border-0 text-[10px]">Suporte</Badge>
                            )}
                            <span className="text-xs text-zinc-500">{msg.user_name}</span>
                            <span className="text-xs text-zinc-400">{formatDate(msg.created_at)}</span>
                          </div>
                          <p className="text-sm text-zinc-700">{msg.content}</p>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>

                  {/* Reply (only if not closed) */}
                  {selectedTicket.status !== 'closed' && (
                    <div className="flex gap-2">
                      <Textarea
                        placeholder="Digite sua mensagem..."
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        className="min-h-[60px]"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            sendMessage();
                          }
                        }}
                      />
                      <Button 
                        onClick={sendMessage}
                        disabled={!newMessage.trim() || sending}
                        className="px-4"
                      >
                        <Send className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            ) : (
              <Card className="border-zinc-200 h-full flex items-center justify-center min-h-[400px]">
                <div className="text-center p-8">
                  <HeadphonesIcon className="w-12 h-12 mx-auto mb-4 text-zinc-300" />
                  <p className="text-zinc-500">Selecione um ticket para ver os detalhes</p>
                </div>
              </Card>
            )}
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
